// PricingPage.jsx
//
// Displays four plans built around SUPPORT LEVEL — how much hands-on
// help a student gets — rather than course-track coverage:
//
//   Self-Study       — full course access + practice tools, learn at
//                       your own pace, no scheduled sessions
//   Partially Guided — Self-Study + guided environment setup and live
//                       practical demos on real tasks
//   Fully Guided     — Partially Guided + 1-on-1 mentorship and
//                       production project / deployment support
//   Enterprise       — team/school seats, admin dashboard, custom
//                       curriculum (promised in this file's own header
//                       comment since the original version, but the
//                       PLANS array below only ever had 3 entries —
//                       added for real this pass)
//
// SCOPING CHANGE (confirmed with Hazzan — see chat):
// Every plan now unlocks ONE course track by default, chosen below. The
// list of tracks comes from utils/courseCatalog.js — the same catalog the
// AI advisor, signup form and Course Hub read — so adding a track there
// adds it here automatically. The old copy claimed
// "Access to all 16 course tracks" on every single plan — that stopped
// being true the moment CourseHubDashboard.jsx started gating courses
// by enrolledTracks. All-Access is now an explicit opt-in upsell
// toggle instead of something every $49/mo plan silently included.
//
// PRICE ANCHORS UNCHANGED: $49 / $99 / $149 are kept exactly as they
// were in the original file — only what each tier includes changed,
// not what it costs. Enterprise has no set price (quote-based), so it
// gets a "Contact Sales" CTA instead of a price tag.
//
// "Get Started" goes straight to /signup (buildCheckoutLink below) with
// plan + supportLevel + track + allAccess as query params — the same
// destination the AI advisor uses (/signup?track=&title=). SignUp.jsx
// reads those params and writes enrolledTracks / supportLevel /
// allAccess to the new user's Firestore doc, which is what
// AuthHelpers.getUserAccess() and the Course Hub gate on. (It used to
// point at /program-training, the original page's target, which was
// carried over unexamined and sat between the plan choice and signup.)
//
// The "support@prodevconsult.com" address on the Enterprise button
// below is a placeholder — swap it for whatever inbox should actually
// receive these inquiries.
// ============================================================

import React, { useState } from "react";
import {
    Grid, Card, CardContent, Typography,
    Button, Avatar, Box, styled, Chip,
    Switch, FormControlLabel,
} from "@mui/material";
import { Link } from "react-router-dom";
import { Fade } from "react-awesome-reveal";

// IMPORT PATH: assumes this file lives in src/pages/CompanyPages/. If it
// lives elsewhere, adjust the number of "../" to reach src/utils/.
import { TRACKS } from "../utils/courseCatalog";

import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ApartmentIcon from "@mui/icons-material/Apartment";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";

// ============================================================
//  CONSTANTS
// ============================================================

const BG_IMAGE = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

// Built from the shared catalog (utils/courseCatalog.js) — nothing to
// keep in sync by hand.
const TRACK_OPTIONS = TRACKS.map((t) => ({ id: t.id, label: t.short }));

// ============================================================
//  STYLED COMPONENTS  (unchanged from the original page)
// ============================================================

const PageWrapper = styled(Box)(() => ({
    minHeight: "100vh",
    paddingTop: "120px",
    paddingBottom: "60px",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.88), rgba(16,7,32,0.88)), url(${BG_IMAGE})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    fontFamily: "'Poppins', sans-serif",
}));

const PricingCard = styled(Card)(({ featured }) => ({
    backgroundColor: featured ? "#09122C" : "rgba(255,255,255,0.97)",
    borderRadius: "18px",
    padding: "20px",
    border: featured ? "2px solid goldenrod" : "1px solid rgba(0,0,0,0.08)",
    boxShadow: featured ? "0 8px 32px rgba(218,165,32,0.2)" : "none",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: featured
            ? "0 12px 40px rgba(218,165,32,0.3)"
            : "0 6px 20px rgba(0,0,0,0.12)",
    },
}));

const GetStartedButton = styled(Button)(({ featured }) => ({
    backgroundColor: featured ? "goldenrod" : "#100720",
    color: featured ? "#09122C" : "goldenrod",
    fontWeight: 700,
    padding: "10px",
    borderRadius: "10px",
    "&:hover": {
        backgroundColor: featured ? "#f0d060" : "#1a0b3a",
    },
}));

// ============================================================
//  PRICING DATA — rebuilt around support level, not track coverage
// ============================================================

const PLANS = [
    {
        id: "self-study",
        supportLevel: "self-study",
        title: "Self-Study",
        icon: <SelfImprovementIcon />,
        price: "$49",
        period: "/month",
        featured: false,
        badge: null,
        tagline: "Learn at your own pace, fully self-directed.",
        benefits: [
            "Full access to your chosen course track",
            "Dedicated premium user dashboard",
            "Code snippets & practice exercises",
            "Inbuilt online compiler — no local setup needed",
            "Assignments with detailed solutions",
            "Extensive Data Structures & Algorithms training",
            "Goal setting tools",
            "Online exams & certification awards",
        ],
    },
    {
        id: "partially-guided",
        supportLevel: "partially-guided",
        title: "Partially Guided",
        icon: <SupportAgentIcon />,
        price: "$99",
        period: "/month",
        featured: true,                 // Highlighted as most popular
        badge: "Most Popular",
        tagline: "We help you get set up, then check in along the way.",
        benefits: [
            "Everything in Self-Study",
            "Guided environment setup — we walk you through it",
            "Live practical demos on real tasks",
            "Scheduled check-ins with an instructor",
            "Priority response when you're stuck",
        ],
    },
    {
        id: "fully-guided",
        supportLevel: "fully-guided",
        title: "Fully Guided",
        icon: <WorkspacePremiumIcon />,
        price: "$149",
        period: "/month",
        featured: false,
        badge: "Best Value",
        tagline: "Hands-on mentorship from first line of code to deployment.",
        benefits: [
            "Everything in Partially Guided",
            "1-on-1 mentorship throughout your track",
            "Production project development & deployment support",
            "Real-world portfolio projects reviewed by a mentor",
            "Guided deployment to live environments",
        ],
    },
    {
        id: "enterprise",
        supportLevel: null, // Seat-based, not a single support level — see tagline/benefits below.
        title: "Enterprise",
        icon: <ApartmentIcon />,
        price: "Custom",
        period: "",
        featured: false,
        badge: "For Teams",
        tagline: "For schools and teams onboarding multiple learners.",
        benefits: [
            "Team / school access for multiple learners",
            "Admin dashboard to manage seats & progress",
            "Curriculum customization",
            "Dedicated instructor support",
            "All-Access to every track included",
        ],
        contactOnly: true,
    },
];

// ============================================================
//  MAIN COMPONENT
// ============================================================

const PricingPage = () => {
    const [selectedTrack, setSelectedTrack] = useState("fullstack");
    const [allAccess, setAllAccess] = useState(false);

    const buildCheckoutLink = (plan) => {
        const params = new URLSearchParams({
            plan: plan.id,
            ...(plan.supportLevel ? { supportLevel: plan.supportLevel } : {}),
            track: selectedTrack,
            allAccess: String(allAccess),
        });
        return `/signup?${params.toString()}`;
    };

    return (
        <PageWrapper>

            {/* ── Page Header ── */}
            <Fade triggerOnce>
                <Box sx={{ px: 2 }}>
                    <Typography variant="h4" align="center" fontWeight={800}
                                color="goldenrod" gutterBottom>
                        Choose the Plan That Fits You
                    </Typography>
                    <Typography variant="subtitle1" align="center" color="white" sx={{ mb: 1 }}>
                        Professional software development training, with as much — or as little —
                        guidance as you need.
                    </Typography>
                    <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.65)", mb: 4 }}>
                        Every plan includes one course track below. Need everything? Add All-Access.
                    </Typography>
                </Box>
            </Fade>

            {/* ── Track + All-Access selector ── */}
            <Fade triggerOnce>
                <Box sx={{
                    display: "flex", flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center", justifyContent: "center",
                    gap: 3, mb: 5, px: 2,
                }}>
                    <Box sx={{ textAlign: "center" }}>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block", mb: 1 }}>
                            Your course track
                        </Typography>
                        {/* Chips wrap onto extra rows, which suits 8 tracks better
                            than a single-row toggle group. */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1, maxWidth: 560 }}>
                            {TRACK_OPTIONS.map((track) => {
                                const selected = selectedTrack === track.id;
                                return (
                                    <Chip
                                        key={track.id}
                                        label={track.label}
                                        clickable
                                        onClick={() => setSelectedTrack(track.id)}
                                        sx={{
                                            fontWeight: 700,
                                            color: selected ? "#09122C" : "white",
                                            backgroundColor: selected ? "goldenrod" : "transparent",
                                            border: "1px solid",
                                            borderColor: selected ? "goldenrod" : "rgba(255,255,255,0.3)",
                                            "&:hover": {
                                                backgroundColor: selected ? "#f0d060" : "rgba(255,255,255,0.1)",
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    </Box>

                    <FormControlLabel
                        sx={{ color: "white" }}
                        control={
                            <Switch
                                checked={allAccess}
                                onChange={(e) => setAllAccess(e.target.checked)}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: "goldenrod" },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "goldenrod" },
                                }}
                            />
                        }
                        label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <AllInclusiveIcon sx={{ fontSize: 18, color: "goldenrod" }} />
                                <Typography variant="body2">
                                    All-Access — unlock every track
                                </Typography>
                            </Box>
                        }
                    />
                </Box>
            </Fade>

            {/* ── Plan Cards ── */}
            <Grid container spacing={4} justifyContent="center" sx={{ px: { xs: 2, md: 6 } }}>
                {PLANS.map((plan, index) => (
                    <Grid item xs={12} sm={6} md={3} key={plan.id}>
                        <Fade delay={index * 150} triggerOnce>
                            <Box sx={{ height: "100%" }}>
                                <PricingCard featured={plan.featured ? 1 : 0}>
                                    <CardContent sx={{ pb: 1 }}>

                                        {/* Badge */}
                                        {plan.badge && (
                                            <Chip
                                                label={plan.badge}
                                                size="small"
                                                sx={{
                                                    mb: 1.5,
                                                    backgroundColor: "goldenrod",
                                                    color: "#09122C",
                                                    fontWeight: 800,
                                                    fontSize: "0.7rem",
                                                }}
                                            />
                                        )}

                                        {/* Icon Avatar */}
                                        <Avatar sx={{
                                            bgcolor: plan.featured ? "goldenrod" : "#100720",
                                            color: plan.featured ? "#09122C" : "goldenrod",
                                            width: 56, height: 56,
                                            mb: 2, fontSize: "22px",
                                        }}>
                                            {plan.icon}
                                        </Avatar>

                                        {/* Plan Title */}
                                        <Typography variant="h6" fontWeight={700} gutterBottom
                                                    sx={{ color: plan.featured ? "white" : "#09122C" }}>
                                            {plan.title}
                                        </Typography>

                                        {/* Tagline */}
                                        <Typography variant="body2" sx={{
                                            mb: 1.5,
                                            color: plan.featured ? "rgba(255,255,255,0.7)" : "#666",
                                            fontStyle: "italic",
                                        }}>
                                            {plan.tagline}
                                        </Typography>

                                        {/* Price */}
                                        <Typography variant="h4" fontWeight={800} sx={{
                                            mb: 2,
                                            color: plan.featured ? "goldenrod" : "#09122C",
                                        }}>
                                            {plan.price}
                                            <Typography component="span"
                                                        sx={{
                                                            fontSize: "1rem", fontWeight: 500,
                                                            color: plan.featured ? "rgba(255,255,255,0.6)" : "#666",
                                                        }}>
                                                {plan.period}
                                            </Typography>
                                        </Typography>

                                        {/* Benefits List */}
                                        <Box sx={{ mt: 1 }}>
                                            {plan.benefits.map((item, i) => (
                                                <Box key={i} display="flex" alignItems="flex-start"
                                                     gap={1} mb={1}>
                                                    <CheckCircleOutlineIcon
                                                        sx={{
                                                            fontSize: 17, mt: "2px", flexShrink: 0,
                                                            color: plan.featured ? "goldenrod" : "#10b981",
                                                        }}
                                                    />
                                                    <Typography variant="body2"
                                                                sx={{
                                                                    color: plan.featured
                                                                        ? "rgba(255,255,255,0.85)"
                                                                        : "#333",
                                                                    lineHeight: 1.5,
                                                                }}>
                                                        {item}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </CardContent>

                                    {/* CTA Button */}
                                    <Box sx={{ p: 2, pt: 0 }}>
                                        {plan.contactOnly ? (
                                            <GetStartedButton
                                                fullWidth
                                                featured={plan.featured ? 1 : 0}
                                                component="a"
                                                href="mailto:support@prodevconsult.com?subject=Enterprise%20Plan%20Inquiry"
                                            >
                                                Contact Sales
                                            </GetStartedButton>
                                        ) : (
                                            <Link to={buildCheckoutLink(plan)} style={{ textDecoration: "none" }}>
                                                <GetStartedButton fullWidth featured={plan.featured ? 1 : 0}>
                                                    Get Started
                                                </GetStartedButton>
                                            </Link>
                                        )}
                                    </Box>

                                </PricingCard>
                            </Box>
                        </Fade>
                    </Grid>
                ))}
            </Grid>

        </PageWrapper>
    );
};

export default PricingPage;
